import { Search, MoreHorizontal, SlidersHorizontal, Check } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const menuStyle = { background: "var(--bg-alt)", borderColor: "var(--border-strong)", color: "var(--ink)" };

export function MoreMenu({ items, testid = "toolbar-more" }) {
  if (!items?.length) return null;
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger className="h-10 w-10 shrink-0 rounded-full flex items-center justify-center outline-none transition-colors hover:bg-[var(--surface-hover)]" style={{ border: "1px solid var(--border-strong)", color: "var(--ink-2)" }} aria-label="More actions" data-testid={testid}>
        <MoreHorizontal size={16} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56" style={menuStyle}>
        {items.map(({ label, icon: Icon, onClick, testid: t }) => (
          <DropdownMenuItem key={label} onSelect={onClick} className="cursor-pointer flex items-center gap-2 focus:bg-[var(--surface-hover)]" data-testid={t}>
            {Icon && <Icon size={14} />} {label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function FilterMenu({ groups, activeCount = 0, testid = "toolbar-filter" }) {
  if (!groups?.length) return null;
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger className="h-10 shrink-0 px-3.5 rounded-full flex items-center gap-1.5 text-sm outline-none transition-colors hover:bg-[var(--surface-hover)]" style={{ border: `1px solid ${activeCount ? "var(--gold)" : "var(--border-strong)"}`, color: activeCount ? "var(--gold)" : "var(--ink-2)" }} data-testid={testid}>
        <SlidersHorizontal size={14} /> <span className="hidden sm:inline">Filter</span>{activeCount > 0 && <span className="text-xs">({activeCount})</span>}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60 max-h-[70vh] overflow-y-auto" style={menuStyle}>
        {groups.map((g, gi) => (
          <div key={g.label}>
            {gi > 0 && <DropdownMenuSeparator style={{ background: "var(--border-subtle)" }} />}
            <DropdownMenuLabel className="text-[0.6rem] uppercase tracking-[0.2em]" style={{ color: "var(--muted)" }}>{g.label}</DropdownMenuLabel>
            {g.options.map((o) => (
              <DropdownMenuItem key={o.value} onSelect={() => g.onChange(o.value)} className="cursor-pointer flex items-center justify-between focus:bg-[var(--surface-hover)]" data-testid={`${testid}-${g.key}-${String(o.value).replace(/[^a-zA-Z0-9]+/g, "-")}`}>
                <span>{o.label}{o.count !== undefined && <span className="ml-1.5 text-xs" style={{ color: "var(--muted)" }}>{o.count}</span>}</span>
                {g.value === o.value && <Check size={14} style={{ color: "var(--gold)" }} />}
              </DropdownMenuItem>
            ))}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Search + primary action + filter + "more" menu, in one clean row.
export function TrackerToolbar({ search, onSearch, placeholder = "Search…", primary, filters, filterCount, more, testidPrefix = "tracker", searchTestid }) {
  return (
    <div className="flex items-center gap-2" data-testid={`${testidPrefix}-toolbar`}>
      <div className="relative flex-1 min-w-0">
        <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--muted)" }} />
        <input value={search} onChange={(e) => onSearch(e.target.value)} placeholder={placeholder} className="w-full h-10 pl-10 pr-3 text-sm rounded-full" style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }} data-testid={searchTestid || `${testidPrefix}-search`} />
      </div>
      <FilterMenu groups={filters} activeCount={filterCount} testid={`${testidPrefix}-filter`} />
      <MoreMenu items={more} testid={`${testidPrefix}-more`} />
      {primary}
    </div>
  );
}

export function TabBar({ tabs, value, onChange, testidPrefix = "tracker-tab" }) {
  return (
    <div className="flex gap-1 overflow-x-auto -mx-1 px-1 pb-0.5" role="tablist" data-testid={`${testidPrefix}-bar`}>
      {tabs.map((t) => {
        const on = value === t.key;
        return (
          <button key={t.key} role="tab" aria-selected={on} onClick={() => onChange(t.key)} className="shrink-0 flex items-center gap-1.5 text-sm px-4 py-2 rounded-full transition-colors" style={{ background: on ? (t.color || "var(--ink)") : "transparent", color: on ? "var(--bg-base)" : "var(--ink-2)" }} data-testid={`${testidPrefix}-${t.key}`}>
            {t.label}
            {t.count !== undefined && <span className="text-xs px-1.5 rounded-full" style={{ background: on ? "rgba(0,0,0,0.15)" : "var(--surface-hover)" }}>{t.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

export function SummaryBar({ items, testid = "tracker-summary" }) {
  return (
    <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 rounded-xl overflow-hidden" style={{ border: "1px solid var(--border-subtle)", background: "var(--bg-alt)" }} data-testid={testid}>
      {items.map((it, i) => {
        const Tag = it.onClick ? "button" : "div";
        return (
          <Tag key={it.label} onClick={it.onClick} className={`text-left px-4 py-3 transition-colors hover:bg-[var(--surface-hover)] ${i > 1 ? "border-t sm:border-t-0" : ""} ${i % 2 ? "border-l" : i > 0 ? "sm:border-l" : ""}`} style={{ borderColor: "var(--border-subtle)" }} data-testid={it.testid}>
            <div className="text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>{it.label}</div>
            <div className="font-serif-display text-2xl leading-tight mt-0.5" style={{ color: it.color || "var(--ink)" }}>{it.value}</div>
          </Tag>
        );
      })}
    </div>
  );
}
